import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { LazyStyleInput, LazyStyleRef, LazyStylesheet } from './lazy-style.model';

@Injectable({
  providedIn: 'root',
})
export class LazyStylesService {
  private document = inject<Document>(DOCUMENT);
  private pendingStyles = new Map<string, Promise<void>>();

  loadMany(styles: LazyStyleInput): Promise<void> {
    const styleList = Array.isArray(styles) ? styles : [styles];
    return Promise.all(styleList.map((style) => this.load(style))).then(() => undefined);
  }

  load(style: LazyStyleRef): Promise<void> {
    const stylesheet = this.normalizeStyle(style);
    const id = stylesheet.id ?? this.createId(stylesheet.href);

    if (this.document.getElementById(id)) {
      return Promise.resolve();
    }

    const existing = this.pendingStyles.get(id);
    if (existing) {
      return existing;
    }

    const promise = new Promise<void>((resolve, reject) => {
      const link = this.document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = stylesheet.href;
      link.dataset['cueLazyStyle'] = 'true';
      link.addEventListener('load', () => resolve(), { once: true });
      link.addEventListener('error', () => reject(new Error(`Failed to load stylesheet: ${stylesheet.href}`)), {
        once: true,
      });
      this.document.head.appendChild(link);
    }).finally(() => {
      this.pendingStyles.delete(id);
    });

    this.pendingStyles.set(id, promise);
    return promise;
  }

  private normalizeStyle(style: LazyStyleRef): LazyStylesheet {
    return typeof style === 'string' ? { href: style } : style;
  }

  private createId(href: string): string {
    return `cue-lazy-style-${href.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  }
}
